<!-- ── Page ───────────────────────────────────────────────────────────── -->
<div class="wizard-page">

  <div class="wizard-header">
    <button mat-icon-button type="button" (click)="router.navigate(['/create'])" matTooltip="Back to Resource Creator">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <h2 class="wizard-title">New VM Template</h2>
  </div>

  <mat-card class="wizard-card">
    <mat-horizontal-stepper [linear]="true" #stepper>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 1 — Template Details                                      -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="templateDetailsForm">
        <ng-template matStepLabel>
          <span matTooltip="A clear name helps instructors quickly identify the right template when scheduling classes"
            matTooltipPosition="below">Template Details</span>
        </ng-template>

        <app-template-details-step [form]="templateDetailsForm"></app-template-details-step>

        <div class="step-actions">
          <span></span>
          <button mat-raised-button color="primary"
            [disabled]="templateDetailsForm.invalid || templateDetailsForm.pending" matStepperNext>
            Next <mat-icon iconPositionEnd>arrow_forward</mat-icon>
          </button>
        </div>
      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 2 — Cloud Provider                                        -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="cloudProviderForm">
        <ng-template matStepLabel>
          <span
            matTooltip="Choosing the right region and size ensures VMs are provisioned close to your users with sufficient compute resources"
            matTooltipPosition="below">Cloud Provider</span>
        </ng-template>

        <form [formGroup]="cloudProviderForm" class="step-form">

          <!-- ── Provider tile — Azure only ── -->
          <div class="section-label">
            <mat-icon class="section-icon">cloud</mat-icon>
            <span>Cloud Provider</span>
          </div>
          <div class="provider-row">
            <div class="provider-tile provider-tile--selected">
              <img src="assets/images/resources/cloudprovider/azure.png" alt="Microsoft Azure" class="provider-logo"
                onerror="this.style.display='none'; this.nextElementSibling.style.display='inline-flex'" />
              <mat-icon class="provider-icon" style="display:none">cloud</mat-icon>
              <span class="provider-name">Microsoft Azure</span>
              <mat-icon class="provider-check">check_circle</mat-icon>
            </div>
            <span class="provider-note">Additional cloud providers will be available in a future release.</span>
          </div>

          <!-- ── Region ── -->
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Region *</mat-label>
            <mat-icon matPrefix>location_on</mat-icon>
            <input matInput [matAutocomplete]="regionAuto" [(ngModel)]="regionSearch"
              [ngModelOptions]="{standalone: true}" (ngModelChange)="filterRegions($event)"
              (blur)="validateCloudFieldOnBlur('region')" placeholder="Search region…" autocomplete="off">
            <mat-autocomplete #regionAuto [displayWith]="displayPairFn"
              (optionSelected)="onRegionSelected($event.option.value)">
              <mat-option *ngFor="let r of filteredRegions" [value]="r">{{ r.name }}</mat-option>
            </mat-autocomplete>
            <mat-error
              *ngIf="cloudProviderForm.get('region')?.hasError('required') && cloudProviderForm.get('region')?.touched">
              Region is required
            </mat-error>
          </mat-form-field>

          <!-- ── VM Size ── -->
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>VM Size *</mat-label>
            <mat-icon matPrefix>memory</mat-icon>
            <input matInput [matAutocomplete]="sizeAuto" [(ngModel)]="sizeSearch" [ngModelOptions]="{standalone: true}"
              (ngModelChange)="filterSizes($event)" (blur)="validateCloudFieldOnBlur('size')" placeholder="Search size…"
              autocomplete="off">
            <mat-autocomplete #sizeAuto [displayWith]="displayPairFn"
              (optionSelected)="onSizeSelected($event.option.value)">
              <mat-option *ngFor="let s of filteredSizes" [value]="s">{{ s.name }}</mat-option>
            </mat-autocomplete>
            <mat-error
              *ngIf="cloudProviderForm.get('size')?.hasError('required') && cloudProviderForm.get('size')?.touched">
              VM size is required
            </mat-error>
          </mat-form-field>

          <!-- ── Storage ── -->
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Storage Type *</mat-label>
            <mat-icon matPrefix>storage</mat-icon>
            <input matInput [matAutocomplete]="storageAuto" [(ngModel)]="storageSearch"
              [ngModelOptions]="{standalone: true}" (ngModelChange)="filterStorages($event)"
              (blur)="validateCloudFieldOnBlur('storage')" placeholder="Search storage type…" autocomplete="off">
            <mat-autocomplete #storageAuto [displayWith]="displayPairFn"
              (optionSelected)="onStorageSelected($event.option.value)">
              <mat-option *ngFor="let s of filteredStorages" [value]="s">{{ s.name }}</mat-option>
            </mat-autocomplete>
            <mat-error
              *ngIf="cloudProviderForm.get('storage')?.hasError('required') && cloudProviderForm.get('storage')?.touched">
              Storage type is required
            </mat-error>
          </mat-form-field>

        </form>

        <div class="step-actions">
          <button mat-stroked-button matStepperPrevious type="button">
            <mat-icon>arrow_back</mat-icon> Back
          </button>
          <button mat-raised-button color="primary" [disabled]="cloudProviderForm.invalid" matStepperNext>
            Next <mat-icon iconPositionEnd>arrow_forward</mat-icon>
          </button>
        </div>
      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 3 — Software Configuration                               -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="softwareForm">
        <ng-template matStepLabel>
          <span
            matTooltip="Pre-installing software saves setup time — students get a ready-to-use environment the moment their VM starts"
            matTooltipPosition="below">Software</span>
        </ng-template>

        <form [formGroup]="softwareForm" class="step-form">

          <!-- ── OS tiles ── -->
          <div class="section-label">
            <mat-icon class="section-icon">computer</mat-icon>
            <span>Operating System</span>
          </div>
          <div class="os-row">
            <div *ngFor="let os of osOptions" class="os-tile" [class.os-tile--selected]="os.id === selectedOs"
              (click)="onOsSelect(os.id)">
              <img class="os-icon-img" [src]="'assets/images/resources/software/os/' + os.id + '.png'"
                [alt]="os.name" />
              <span class="os-name">{{ os.name }}</span>
              <mat-icon *ngIf="os.id === selectedOs" class="os-check">check_circle</mat-icon>
            </div>
          </div>

          <!-- ── OS Version + Hyper-V (same row) ── -->
          <div class="os-hyperv-row">
            <mat-form-field appearance="outline" class="form-field-grow">
              <mat-label>OS Version *</mat-label>
              <mat-icon matPrefix>featured_play_list</mat-icon>
              <input matInput [matAutocomplete]="osVersionAuto" [(ngModel)]="osVersionSearch"
                [ngModelOptions]="{standalone: true}" (ngModelChange)="filterOsVersions($event)"
                (blur)="validateOsVersionOnBlur()" placeholder="Select OS version…" autocomplete="off">
              <mat-autocomplete #osVersionAuto [displayWith]="displayPairFn"
                (optionSelected)="onOsVersionSelected($event.option.value)">
                <mat-option *ngFor="let v of filteredOsVersions" [value]="v">{{ v.name }}</mat-option>
              </mat-autocomplete>
              <mat-error
                *ngIf="softwareForm.get('osVersion')?.hasError('required') && softwareForm.get('osVersion')?.touched">
                OS version is required
              </mat-error>
            </mat-form-field>

            <div class="hyperv-inline" *ngIf="!isLinux">
              <mat-icon class="section-icon">developer_board</mat-icon>
              <span>Hyper-V Virtualization</span>
              <mat-slide-toggle formControlName="hyperV" color="primary"></mat-slide-toggle>
            </div>
          </div>

          <!-- ── SDKs & Frameworks ── -->
          <div class="section-label">
            <mat-icon class="section-icon">code</mat-icon>
            <span>SDKs &amp; Frameworks <span class="optional-badge">optional</span></span>
          </div>
          <div class="chip-field">
            <mat-chip-grid #sdkChipGrid>
              <mat-chip-row *ngFor="let id of selectedSdks" (removed)="removeChip('sdks', id)">
                {{ getSdkLabel(id) }}
                <button matChipRemove><mat-icon>cancel</mat-icon></button>
              </mat-chip-row>
            </mat-chip-grid>
            <input placeholder="Add SDK…" [matAutocomplete]="sdkAuto" [matChipInputFor]="sdkChipGrid"
              [(ngModel)]="sdkSearch" [ngModelOptions]="{standalone: true}" (ngModelChange)="filterSdks($event)"
              #sdkInput>
            <mat-autocomplete #sdkAuto (optionSelected)="addChip('sdks', $event.option.value, sdkInput)">
              <mat-option *ngFor="let o of filteredSdkOptions" [value]="o">{{ o.name }}</mat-option>
            </mat-autocomplete>
          </div>

          <!-- ── Databases ── -->
          <div class="section-label">
            <mat-icon class="section-icon">dns</mat-icon>
            <span>Databases <span class="optional-badge">optional</span></span>
          </div>
          <div class="chip-field">
            <mat-chip-grid #dbChipGrid>
              <mat-chip-row *ngFor="let id of selectedDatabases" (removed)="removeChip('databases', id)">
                {{ getDbLabel(id) }}
                <button matChipRemove><mat-icon>cancel</mat-icon></button>
              </mat-chip-row>
            </mat-chip-grid>
            <input placeholder="Add database…" [matAutocomplete]="dbAuto" [matChipInputFor]="dbChipGrid"
              [(ngModel)]="dbSearch" [ngModelOptions]="{standalone: true}" (ngModelChange)="filterDbs($event)" #dbInput>
            <mat-autocomplete #dbAuto (optionSelected)="addChip('databases', $event.option.value, dbInput)">
              <mat-option *ngFor="let o of filteredDbOptions" [value]="o">{{ o.name }}</mat-option>
            </mat-autocomplete>
          </div>

          <!-- ── Development Tools ── -->
          <div class="section-label">
            <mat-icon class="section-icon">build</mat-icon>
            <span>IDE Editors<span class="optional-badge">optional</span></span>
          </div>
          <div class="chip-field">
            <mat-chip-grid #editorChipGrid>
              <mat-chip-row *ngFor="let id of selectedEditors" (removed)="removeChip('editors', id)">
                {{ getEditorLabel(id) }}
                <button matChipRemove><mat-icon>cancel</mat-icon></button>
              </mat-chip-row>
            </mat-chip-grid>
            <input placeholder="Add editor…" [matAutocomplete]="editorAuto" [matChipInputFor]="editorChipGrid"
              [(ngModel)]="editorSearch" [ngModelOptions]="{standalone: true}" (ngModelChange)="filterEditors($event)"
              #editorInput>
            <mat-autocomplete #editorAuto (optionSelected)="addChip('editors', $event.option.value, editorInput)">
              <mat-option *ngFor="let o of filteredEditorOptions" [value]="o">{{ o.name }}</mat-option>
            </mat-autocomplete>
          </div>

          <!-- ── Miscellaneous ── -->
          <div class="section-label">
            <mat-icon class="section-icon">apps</mat-icon>
            <span>Miscellaneous & Tools<span class="optional-badge">optional</span></span>
          </div>
          <div class="chip-field">
            <mat-chip-grid #miscChipGrid>
              <mat-chip-row *ngFor="let id of selectedMisc" (removed)="removeChip('misc', id)">
                {{ getMiscLabel(id) }}
                <button matChipRemove><mat-icon>cancel</mat-icon></button>
              </mat-chip-row>
            </mat-chip-grid>
            <input placeholder="Add misc software…" [matAutocomplete]="miscAuto" [matChipInputFor]="miscChipGrid"
              [(ngModel)]="miscSearch" [ngModelOptions]="{standalone: true}" (ngModelChange)="filterMisc($event)"
              #miscInput>
            <mat-autocomplete #miscAuto (optionSelected)="addChip('misc', $event.option.value, miscInput)">
              <mat-option *ngFor="let o of filteredMiscOptions" [value]="o">{{ o.name }}</mat-option>
            </mat-autocomplete>
          </div>

        </form>

        <div class="step-actions">
          <button mat-stroked-button matStepperPrevious type="button">
            <mat-icon>arrow_back</mat-icon> Back
          </button>
          <button mat-raised-button color="primary" [disabled]="softwareForm.invalid" matStepperNext>
            Review <mat-icon iconPositionEnd>arrow_forward</mat-icon>
          </button>
        </div>
      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 4 — Review & Create                                       -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step>
        <ng-template matStepLabel>
          <span
            matTooltip="Templates are reused across many classes — a final check here prevents costly re-provisioning later"
            matTooltipPosition="below">Review &amp; Create</span>
        </ng-template>

        <mat-accordion class="review-accordion" [multi]="true">

          <!-- ── Template Details panel ── -->
          <mat-expansion-panel #panelTD class="review-panel" [expanded]="false">
            <mat-expansion-panel-header>
              <mat-panel-title class="review-panel-title">
                <mat-icon class="review-panel-icon">label</mat-icon>
                Template Details
              </mat-panel-title>
              <mat-panel-description *ngIf="!panelTD.expanded">
                <div class="review-panel-summary">
                  <span class="rps-item"><mat-icon class="rps-icon">label</mat-icon>{{ reviewTemplateName }}</span>
                </div>
              </mat-panel-description>
            </mat-expansion-panel-header>
            <div class="review-grid">
              <span class="review-label"><mat-icon class="rl-icon">label</mat-icon>Name</span>
              <span class="review-value">{{ reviewTemplateName }}</span>
              <span class="review-label"><mat-icon class="rl-icon">description</mat-icon>Description</span>
              <span class="review-value">{{ reviewTemplateDesc || '—' }}</span>
            </div>
          </mat-expansion-panel>

          <!-- ── Cloud Provider panel ── -->
          <mat-expansion-panel #panelCP class="review-panel" [expanded]="false">
            <mat-expansion-panel-header>
              <mat-panel-title class="review-panel-title">
                <mat-icon class="review-panel-icon">cloud</mat-icon>
                Cloud Provider
              </mat-panel-title>
              <mat-panel-description *ngIf="!panelCP.expanded">
                <div class="review-panel-summary">
                  <span class="rps-item"><mat-icon class="rps-icon">cloud</mat-icon>Azure</span>
                  <span class="rps-item"><mat-icon class="rps-icon">location_on</mat-icon>{{ reviewRegionLabel }}</span>
                  <span class="rps-item"><mat-icon class="rps-icon">memory</mat-icon>{{ reviewSizeLabel }}</span>
                </div>
              </mat-panel-description>
            </mat-expansion-panel-header>
            <div class="review-grid">
              <span class="review-label"><mat-icon class="rl-icon">cloud</mat-icon>Provider</span>
              <span class="review-value">Microsoft Azure</span>
              <span class="review-label"><mat-icon class="rl-icon">location_on</mat-icon>Region</span>
              <span class="review-value">{{ reviewRegionLabel }}</span>
              <span class="review-label"><mat-icon class="rl-icon">memory</mat-icon>VM Size</span>
              <span class="review-value">{{ reviewSizeLabel }}</span>
              <span class="review-label"><mat-icon class="rl-icon">storage</mat-icon>Storage</span>
              <span class="review-value">{{ reviewStorageLabel }}</span>
            </div>
          </mat-expansion-panel>

          <!-- ── Software panel ── -->
          <mat-expansion-panel #panelSW class="review-panel" [expanded]="false">
            <mat-expansion-panel-header>
              <mat-panel-title class="review-panel-title">
                <mat-icon class="review-panel-icon">computer</mat-icon>
                Software
              </mat-panel-title>
              <mat-panel-description *ngIf="!panelSW.expanded">
                <div class="review-panel-summary">
                  <span class="rps-item"><mat-icon class="rps-icon">laptop_windows</mat-icon>{{ reviewOsVersionLabel
                    }}</span>
                  <span class="rps-item" *ngIf="!isLinux && softwareForm.value.hyperV"><mat-icon
                      class="rps-icon">developer_board</mat-icon>Hyper-V</span>
                  <span class="rps-item" *ngIf="selectedSdks.length"><mat-icon class="rps-icon">code</mat-icon>{{
                    selectedSdks.length }} SDK{{ selectedSdks.length !== 1 ? 's' : '' }}</span>
                </div>
              </mat-panel-description>
            </mat-expansion-panel-header>

            <div class="review-grid">
              <span class="review-label"><mat-icon class="rl-icon">laptop_windows</mat-icon>OS</span>
              <span class="review-value">{{ reviewOsLabel }}</span>
              <span class="review-label"><mat-icon class="rl-icon">featured_play_list</mat-icon>Version</span>
              <span class="review-value">{{ reviewOsVersionLabel }}</span>
              <ng-container *ngIf="!isLinux">
                <span class="review-label"><mat-icon class="rl-icon">developer_board</mat-icon>Hyper-V</span>
                <span class="review-value">{{ softwareForm.value.hyperV ? 'Enabled' : 'Disabled' }}</span>
              </ng-container>
            </div>

            <div class="review-chip-row" *ngIf="selectedSdks.length">
              <span class="review-chip-label"><mat-icon class="rl-icon">code</mat-icon>SDKs</span>
              <div class="review-chips">
                <span *ngFor="let id of selectedSdks" class="review-chip-badge">{{ getSdkLabel(id) }}</span>
              </div>
            </div>
            <div class="review-chip-row" *ngIf="selectedDatabases.length">
              <span class="review-chip-label"><mat-icon class="rl-icon">dns</mat-icon>Databases</span>
              <div class="review-chips">
                <span *ngFor="let id of selectedDatabases" class="review-chip-badge">{{ getDbLabel(id) }}</span>
              </div>
            </div>
            <div class="review-chip-row" *ngIf="selectedEditors.length">
              <span class="review-chip-label"><mat-icon class="rl-icon">build</mat-icon>IDE Editors</span>
              <div class="review-chips">
                <span *ngFor="let id of selectedEditors" class="review-chip-badge">{{ getEditorLabel(id) }}</span>
              </div>
            </div>
            <div class="review-chip-row" *ngIf="selectedMisc.length">
              <span class="review-chip-label"><mat-icon class="rl-icon">apps</mat-icon>Misc</span>
              <div class="review-chips">
                <span *ngFor="let id of selectedMisc" class="review-chip-badge">{{ getMiscLabel(id) }}</span>
              </div>
            </div>

          </mat-expansion-panel>

        </mat-accordion>

        <div class="step-actions">
          <button mat-stroked-button matStepperPrevious type="button">
            <mat-icon>arrow_back</mat-icon> Back
          </button>
          <button mat-raised-button color="primary" [disabled]="isSubmitting" (click)="onSubmit()">
            <mat-icon *ngIf="!isSubmitting">computer</mat-icon>
            <mat-progress-spinner *ngIf="isSubmitting" mode="indeterminate" diameter="18"
              class="btn-spinner"></mat-progress-spinner>
            {{ isSubmitting ? 'Creating…' : 'Create Template' }}
          </button>
        </div>

      </mat-step>

    </mat-horizontal-stepper>
  </mat-card>

</div>